<template>
  <div class="flex flex-wrap items-center gap-2 py-1">
    <Select
      :model-value="applicationStatusModel"
      @update:model-value="onApplicationStatusChange"
    >
      <SelectTrigger
        class="h-8 rounded-full px-3 text-xs font-medium border bg-white hover:bg-gray-50 transition-colors w-auto gap-1.5"
        :class="applicationStatusModel !== ALL_OPTION_VALUE
          ? 'border-cosmicPurple/40 bg-cosmicPurple/5 text-cosmicPurple'
          : 'border-gray-200 text-gray-600'"
      >
        <Flag class="w-3 h-3 shrink-0" />
        <SelectValue placeholder="Application Status" />
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          <SelectItem :value="ALL_OPTION_VALUE">
            <span class="text-xs">All Application Statuses</span>
          </SelectItem>
        <SelectItem
          v-for="status in applicationStatusOptions"
          :key="status.value"
          :value="status.value"
        >
          <span
            :class="[status.bgColor, status.textColor]"
            class="px-2 py-0.5 rounded-full text-xs"
          >
            {{ status.label }}
          </span>
        </SelectItem>
        </SelectGroup>
      </SelectContent>
    </Select>

    <Select
      :model-value="interviewStatusModel"
      @update:model-value="onInterviewStatusChange"
    >
      <SelectTrigger
        class="h-8 rounded-full px-3 text-xs font-medium border bg-white hover:bg-gray-50 transition-colors w-auto gap-1.5"
        :class="interviewStatusModel !== ALL_OPTION_VALUE
          ? 'border-cosmicPurple/40 bg-cosmicPurple/5 text-cosmicPurple'
          : 'border-gray-200 text-gray-600'"
      >
        <Clock class="w-3 h-3 shrink-0" />
        <SelectValue placeholder="Interview Stage" />
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          <SelectItem :value="ALL_OPTION_VALUE">
            <span class="text-xs">All Interview Stages</span>
          </SelectItem>
        <SelectItem
          v-for="status in interviewStatusOptions"
          :key="status.value"
          :value="status.value"
        >
          <span
            :class="[status.bgColor, status.textColor]"
            class="px-2 py-0.5 rounded-full text-xs"
          >
            {{ status.label }}
          </span>
        </SelectItem>
        </SelectGroup>
      </SelectContent>
    </Select>

    <Select
      :model-value="referralStatusModel"
      @update:model-value="onReferralStatusChange"
    >
      <SelectTrigger
        class="h-8 rounded-full px-3 text-xs font-medium border bg-white hover:bg-gray-50 transition-colors w-auto gap-1.5"
        :class="referralStatusModel !== ALL_OPTION_VALUE
          ? 'border-cosmicPurple/40 bg-cosmicPurple/5 text-cosmicPurple'
          : 'border-gray-200 text-gray-600'"
      >
        <Send class="w-3 h-3 shrink-0" />
        <SelectValue placeholder="Referral Status" />
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          <SelectItem :value="ALL_OPTION_VALUE">
            <span class="text-xs">All Referral Statuses</span>
          </SelectItem>
        <SelectItem
          v-for="status in referralStatusOptions"
          :key="status.value"
          :value="status.value"
        >
          <span
            :class="[status.bgColor, status.textColor]"
            class="px-2 py-0.5 rounded-full text-xs"
          >
            {{ status.label }}
          </span>
        </SelectItem>
        </SelectGroup>
      </SelectContent>
    </Select>

    <!-- Quick-filter group: All / Pending / Submitted -->
    <div class="inline-flex items-center gap-1.5 border-l-[1px] pl-2 border-gray-200">
      <button
        type="button"
        class="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-medium rounded-full border transition-colors"
        :class="isAllActive
          ? 'border-cosmicPurple/40 bg-cosmicPurple/5 text-cosmicPurple'
          : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'"
        @click="emit('select-all')"
      >
        <List class="w-3 h-3 shrink-0" />
        All requests
        <span
          class="ml-0.5 tabular-nums rounded-full px-1.5 py-0.5 text-[10px] font-semibold"
          :class="isAllActive ? 'bg-cosmicPurple/10 text-cosmicPurple' : 'bg-gray-100 text-gray-500'"
        >
          {{ statistics?.total_referrals ?? '–' }}
        </span>
      </button>

      <button
        type="button"
        class="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-medium rounded-full border transition-colors"
        :class="isPendingActive
          ? 'border-indigo-400 bg-indigo-50 text-indigo-700'
          : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'"
        @click="emit('toggle-pending')"
      >
        <Hourglass class="w-3 h-3 shrink-0" />
        Pending
        <span
          class="ml-0.5 tabular-nums rounded-full px-1.5 py-0.5 text-[10px] font-semibold"
          :class="isPendingActive ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-500'"
        >
          {{ pendingCount }}
        </span>
      </button>

      <div
        v-if="isPendingActive"
        class="inline-flex items-center rounded-full border border-indigo-200 bg-white overflow-hidden text-xs"
      >
        <button
          v-for="(days, idx) in TIMEFRAME_OPTIONS"
          :key="days"
          type="button"
          class="h-8 px-2.5 transition-colors"
          :class="[
            timeframeModel === String(days)
              ? 'bg-indigo-100 text-indigo-700 font-semibold'
              : 'text-gray-600 hover:bg-indigo-50',
            idx > 0 ? 'border-l border-indigo-200' : ''
          ]"
          @click="emit('update:timeframe', String(days))"
        >
          {{ days }}d
        </button>
      </div>

      <button
        type="button"
        class="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-medium rounded-full border transition-colors"
        :class="submitted
          ? 'border-emerald-400 bg-emerald-50 text-emerald-700'
          : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'"
        @click="emit('toggle-submitted')"
      >
        <CheckCircle class="w-3 h-3 shrink-0" />
        Confirmed
        <span
          class="ml-0.5 tabular-nums rounded-full px-1.5 py-0.5 text-[10px] font-semibold"
          :class="submitted ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'"
        >
          {{ statistics?.successful_referrals ?? '–' }}
        </span>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { CheckCircle, Clock, Flag, Hourglass, List, Send } from 'lucide-vue-next'
import {
  applicationStatusOptions,
  interviewStatusOptions,
  referralStatusOptions
} from '@/const/applicationStatuses'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import type { ReferrerStatistics } from '@/types/referrerDashboard'

const TIMEFRAME_OPTIONS = [30, 60, 90]
const PENDING_STATUS = 'OUTTALENT_REQUESTED_REFERRAL'
const TIMEFRAME_STATS_KEY: Record<string, keyof ReferrerStatistics> = {
  '30': 'pending_last_30_days',
  '60': 'pending_last_60_days',
  '90': 'pending_last_90_days'
}

const props = defineProps<{
  applicationStatus: string
  interviewStatus: string
  referralStatus: string
  timeframe: string
  submitted: boolean
  statistics: ReferrerStatistics | null
}>()

const emit = defineEmits<{
  'update:applicationStatus': [value: string]
  'update:interviewStatus': [value: string]
  'update:referralStatus': [value: string]
  'update:timeframe': [value: string]
  'toggle-pending': []
  'toggle-submitted': []
  'select-all': []
}>()

const ALL_OPTION_VALUE = '__ALL__'

const applicationStatusModel = computed(() => props.applicationStatus || ALL_OPTION_VALUE)
const interviewStatusModel = computed(() => props.interviewStatus || ALL_OPTION_VALUE)
const referralStatusModel = computed(() => props.referralStatus || ALL_OPTION_VALUE)
const isPendingActive = computed(() => referralStatusModel.value === PENDING_STATUS)
const timeframeModel = computed(() => props.timeframe || '30')
const isAllActive = computed(() => !isPendingActive.value && !props.submitted)

const pendingCount = computed(() => {
  const key = TIMEFRAME_STATS_KEY[timeframeModel.value]
  return key ? (props.statistics?.[key] ?? '–') : (props.statistics?.pending_last_30_days ?? '–')
})

function mapSelectValue(value: unknown) {
  const normalized = String(value || '')
  return normalized === ALL_OPTION_VALUE ? '' : normalized
}

function onApplicationStatusChange(value: unknown) {
  emit('update:applicationStatus', mapSelectValue(value))
}

function onInterviewStatusChange(value: unknown) {
  emit('update:interviewStatus', mapSelectValue(value))
}

function onReferralStatusChange(value: unknown) {
  emit('update:referralStatus', mapSelectValue(value))
}
</script>

